import { clamp } from "../core/math";

export const DEFAULT_CAMERA_YAW_RADIANS = Math.PI * 0.22;

export interface MovementInput {
  readonly x: number;
  readonly z: number;
  readonly boost: boolean;
  readonly dash: boolean;
}

export class InputController {
  private readonly keys = new Set<string>();
  private joystickX = 0;
  private joystickZ = 0;
  private dashLatch = false;
  private dragging = false;
  private pointerX = 0;
  private joystickPointerId: number | null = null;
  private readonly disposers: Array<() => void> = [];

  cameraYaw = DEFAULT_CAMERA_YAW_RADIANS;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly joystick: HTMLElement,
    private readonly joystickKnob: HTMLElement,
    private readonly boostButton: HTMLButtonElement,
  ) {
    this.bindKeyboard();
    this.bindCameraDrag();
    this.bindTouchControls();
  }

  read(): MovementInput {
    let x = (this.keys.has("KeyD") || this.keys.has("ArrowRight") ? 1 : 0)
      - (this.keys.has("KeyA") || this.keys.has("ArrowLeft") ? 1 : 0);
    let z = (this.keys.has("KeyW") || this.keys.has("ArrowUp") ? 1 : 0)
      - (this.keys.has("KeyS") || this.keys.has("ArrowDown") ? 1 : 0);

    x += this.joystickX;
    z += this.joystickZ;

    const gamepad = navigator.getGamepads?.()[0];
    if (gamepad) {
      const gamepadX = Math.abs(gamepad.axes[0] ?? 0) > 0.12 ? gamepad.axes[0] ?? 0 : 0;
      const gamepadZ = Math.abs(gamepad.axes[1] ?? 0) > 0.12 ? -(gamepad.axes[1] ?? 0) : 0;
      x += gamepadX;
      z += gamepadZ;
    }

    const length = Math.hypot(x, z);
    if (length > 1) {
      x /= length;
      z /= length;
    }

    const dash = this.dashLatch || Boolean(gamepad?.buttons[1]?.pressed);
    this.dashLatch = false;
    return {
      x,
      z,
      boost:
        this.keys.has("ShiftLeft")
        || this.keys.has("ShiftRight")
        || Boolean(gamepad?.buttons[0]?.pressed),
      dash,
    };
  }

  dispose(): void {
    for (const dispose of this.disposers) {
      dispose();
    }
    this.disposers.length = 0;
  }

  reset(resetCamera = false): void {
    this.keys.clear();
    this.joystickX = 0;
    this.joystickZ = 0;
    this.dashLatch = false;
    this.dragging = false;
    this.joystickPointerId = null;
    if (resetCamera) this.cameraYaw = DEFAULT_CAMERA_YAW_RADIANS;
    this.joystickKnob.style.transform = "translate(-50%, -50%)";
  }

  private bindKeyboard(): void {
    const keydown = (event: KeyboardEvent): void => {
      if (
        event.code.startsWith("Arrow")
        || event.code === "Space"
        || event.code.startsWith("Shift")
      ) {
        event.preventDefault();
      }
      if (event.code === "Space" && !event.repeat) {
        this.dashLatch = true;
      }
      this.keys.add(event.code);
    };
    const keyup = (event: KeyboardEvent): void => {
      this.keys.delete(event.code);
    };
    const blur = (): void => this.reset();
    const visibility = (): void => {
      if (document.visibilityState !== "visible") {
        this.reset();
      }
    };
    window.addEventListener("keydown", keydown, { passive: false });
    window.addEventListener("keyup", keyup);
    window.addEventListener("blur", blur);
    document.addEventListener("visibilitychange", visibility);
    this.disposers.push(
      () => window.removeEventListener("keydown", keydown),
      () => window.removeEventListener("keyup", keyup),
      () => window.removeEventListener("blur", blur),
      () => document.removeEventListener("visibilitychange", visibility),
    );
  }

  private bindCameraDrag(): void {
    const pointerDown = (event: PointerEvent): void => {
      if (event.pointerType === "touch") {
        return;
      }
      this.dragging = true;
      this.pointerX = event.clientX;
      try {
        this.canvas.setPointerCapture(event.pointerId);
      } catch {
        // Synthetic QA events may not own an active platform pointer.
      }
    };
    const pointerMove = (event: PointerEvent): void => {
      if (!this.dragging) {
        return;
      }
      const deltaX = event.clientX - this.pointerX;
      this.pointerX = event.clientX;
      this.cameraYaw -= deltaX * 0.004;
    };
    const pointerUp = (event: PointerEvent): void => {
      this.dragging = false;
      if (this.canvas.hasPointerCapture(event.pointerId)) {
        this.canvas.releasePointerCapture(event.pointerId);
      }
    };
    const wheel = (event: WheelEvent): void => {
      this.cameraYaw -= clamp(event.deltaX, -80, 80) * 0.0015;
    };
    this.canvas.addEventListener("pointerdown", pointerDown);
    this.canvas.addEventListener("pointermove", pointerMove);
    this.canvas.addEventListener("pointerup", pointerUp);
    this.canvas.addEventListener("pointercancel", pointerUp);
    this.canvas.addEventListener("lostpointercapture", pointerUp);
    this.canvas.addEventListener("wheel", wheel, { passive: true });
    this.disposers.push(
      () => this.canvas.removeEventListener("pointerdown", pointerDown),
      () => this.canvas.removeEventListener("pointermove", pointerMove),
      () => this.canvas.removeEventListener("pointerup", pointerUp),
      () => this.canvas.removeEventListener("pointercancel", pointerUp),
      () => this.canvas.removeEventListener("lostpointercapture", pointerUp),
      () => this.canvas.removeEventListener("wheel", wheel),
    );
  }

  private bindTouchControls(): void {
    const updateJoystick = (event: PointerEvent): void => {
      const bounds = this.joystick.getBoundingClientRect();
      const centerX = bounds.left + bounds.width * 0.5;
      const centerY = bounds.top + bounds.height * 0.5;
      const radius = bounds.width * 0.34;
      let x = (event.clientX - centerX) / radius;
      let y = (event.clientY - centerY) / radius;
      const length = Math.hypot(x, y);
      if (length > 1) {
        x /= length;
        y /= length;
      }
      this.joystickX = x;
      this.joystickZ = -y;
      this.joystickKnob.style.transform = `translate(calc(-50% + ${x * radius}px), calc(-50% + ${y * radius}px))`;
    };
    const joystickDown = (event: PointerEvent): void => {
      this.joystickPointerId = event.pointerId;
      try {
        this.joystick.setPointerCapture(event.pointerId);
      } catch {
        // Synthetic QA events may not own an active platform pointer.
      }
      updateJoystick(event);
    };
    const joystickMove = (event: PointerEvent): void => {
      if (event.pointerId === this.joystickPointerId) {
        updateJoystick(event);
      }
    };
    const joystickUp = (event: PointerEvent): void => {
      if (event.pointerId !== this.joystickPointerId) {
        return;
      }
      this.joystickPointerId = null;
      this.joystickX = 0;
      this.joystickZ = 0;
      this.joystickKnob.style.transform = "translate(-50%, -50%)";
    };
    const boostDown = (event: PointerEvent): void => {
      this.dashLatch = true;
      try {
        this.boostButton.setPointerCapture(event.pointerId);
      } catch {
        // Synthetic QA events may not own an active platform pointer.
      }
    };
    const boostUp = (): void => undefined;

    this.joystick.addEventListener("pointerdown", joystickDown);
    this.joystick.addEventListener("pointermove", joystickMove);
    this.joystick.addEventListener("pointerup", joystickUp);
    this.joystick.addEventListener("pointercancel", joystickUp);
    this.joystick.addEventListener("lostpointercapture", joystickUp);
    this.boostButton.addEventListener("pointerdown", boostDown);
    this.boostButton.addEventListener("pointerup", boostUp);
    this.boostButton.addEventListener("pointercancel", boostUp);
    this.boostButton.addEventListener("lostpointercapture", boostUp);
    this.disposers.push(
      () => this.joystick.removeEventListener("pointerdown", joystickDown),
      () => this.joystick.removeEventListener("pointermove", joystickMove),
      () => this.joystick.removeEventListener("pointerup", joystickUp),
      () => this.joystick.removeEventListener("pointercancel", joystickUp),
      () => this.joystick.removeEventListener("lostpointercapture", joystickUp),
      () => this.boostButton.removeEventListener("pointerdown", boostDown),
      () => this.boostButton.removeEventListener("pointerup", boostUp),
      () => this.boostButton.removeEventListener("pointercancel", boostUp),
      () => this.boostButton.removeEventListener("lostpointercapture", boostUp),
    );
  }
}
